import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DeleteIcon from '@src/svgs/DeleteIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isBarTarget } from './barGate';
import { useFloatingToolbarHost } from './FloatingToolbarHost';
import {
  FloatingToolbarSurface,
  ToolbarDivider,
  ToolbarIconButton,
  ToolbarMenu,
  ToolbarMenuItem,
  ToolbarTextButton,
} from './FloatingToolbarSurface';
import { ToolbarZoomStepper } from './ToolbarStepper';
import { ToolbarLayer } from './ToolbarLayer';
import { useFloatingToolbarPosition } from './useFloatingToolbarPosition';

// Opacity presets mirror the right sidebar's ImageSettings dropdown so the
// toolbar and panel stay consistent.
const OPACITY_OPTIONS = ['100%', '80%', '60%', '40%', '20%'];

// Zoom matches ImageSettings' slider bounds (100–150%); 5% steps give a
// usable stepper without the slider's fine granularity.
const MIN_ZOOM = 100;
const MAX_ZOOM = 150;
const ZOOM_STEP = 5;

const parsePercent = (value: unknown, fallback: number): number => {
  const n = parseFloat(String(value ?? ''));
  return Number.isNaN(n) ? fallback : n;
};

/**
 * Contextual toolbar for image elements (regular photos and logos). Surfaces
 * the most-used ImageSettings controls — Fit, Opacity, Zoom, Delete — without
 * requiring the right sidebar to be open, and offers a "More" affordance that
 * opens the full panel. Every mutation goes through commandUpdateElement /
 * commandRemoveElement so undo/redo works exactly as in the sidebar.
 */
const ImageFloatingToolbar = observer(() => {
  // PLAT-12101 — the surface's host; no host, no bar.
  const host = useFloatingToolbarHost();
  const activeElement = host?.getActiveElement();

  // PLAT-12098 — a single, top-level image, not while playing (DE1 / DE2).
  // An image inside a graphics card is the card's chip's to edit.
  const isImageEl =
    !!host && activeElement?.source?.type === 'image' && isBarTarget(host);

  const elementId = activeElement?.source?.id;
  const { toolbarRef, pos } = useFloatingToolbarPosition(isImageEl, elementId);

  // Displayed values come from the element on every render, never from local
  // state: `getActiveElement()` reads the editor's current element, and as an
  // observer this re-renders on each change — a click here, the sidebar, or
  // undo. Local copies drifted (Contain and 60% still lit after three undos
  // had put the element back to cover and 100%), the same way the font-size
  // stepper once showed 32 for every text (PLAT-12088).
  const src = (activeElement?.source ?? {}) as Record<string, unknown>;
  const fit = typeof src.fit === 'string' ? src.fit : 'cover';
  const opacity = typeof src.opacity === 'string' ? src.opacity : '100%';
  const zoom = parsePercent(src.x_scale, MIN_ZOOM);
  const [opacityOpen, setOpacityOpen] = useState(false);
  const [opacityMenuPos, setOpacityMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // A new selection starts with the menu closed.
  useEffect(() => {
    setOpacityOpen(false);
  }, [elementId]);

  // Close the opacity menu on an outside click, except on its own trigger,
  // whose click toggles it.
  const opacityTriggerRef = useRef<HTMLButtonElement>(null);
  const opacityMenuRef = useRef<HTMLDivElement>(null);
  const closeOpacityOnOutside = useCallback((e: MouseEvent) => {
    const trigger = opacityTriggerRef.current;
    if (trigger && e.target instanceof Node && trigger.contains(e.target)) {
      return;
    }
    setOpacityOpen(false);
  }, []);
  useOutsideAlerter(opacityMenuRef, closeOpacityOnOutside);

  if (!host || !isImageEl || !elementId) return null;

  const editor = host.editorStore?.editor;

  const modify = (patch: Record<string, unknown>) => {
    if (!editor) return;
    editor.commandUpdateElement(elementId, patch);
  };

  const applyFit = (next: string) => modify({ fit: next });

  const applyOpacity = (next: string) => {
    setOpacityOpen(false);
    modify({ opacity: next });
  };

  const applyZoom = (next: number) =>
    modify({ x_scale: `${next}%`, y_scale: `${next}%` });

  // PLAT-12098 DS3 — saved right after the command, as the graphics card
  // chip's Delete is (PLAT-10191: the command's own autosave snapshots the
  // pre-delete state), so the two Deletes never differ after a reload.
  const handleDelete = () => {
    if (!editor) return;
    editor.commandRemoveElement(elementId);
    host.persist();
  };

  // More toggles the floating Media > Photo panel with this image's full
  // settings, the way a double-click on the element does (PLAT-10391): open
  // when it is closed or showing something else, closed when it is already
  // showing these settings — a button that only opens looks dead once the
  // panel is up. The host owns the panel (PLAT-12101); a surface without one
  // gets no More.
  const { settings } = host;
  const isSidebarOnThisElement = !!settings?.isOpen('photo');

  const opacityMenu =
    opacityOpen && opacityMenuPos ? (
      <ToolbarMenu
        ref={opacityMenuRef}
        style={{ top: opacityMenuPos.top, left: opacityMenuPos.left }}
        onMouseDown={e => e.stopPropagation()}
      >
        {OPACITY_OPTIONS.map(opt => (
          <ToolbarMenuItem
            key={opt}
            isSelected={opt === opacity}
            onClick={() => applyOpacity(opt)}
          >
            {opt}
          </ToolbarMenuItem>
        ))}
      </ToolbarMenu>
    ) : null;

  return (
    <ToolbarLayer>
      <FloatingToolbarSurface ref={toolbarRef} pos={pos}>
        {/* Fit */}
        <ToolbarTextButton
          isActive={fit === 'cover'}
          onClick={() => applyFit('cover')}
        >
          Cover
        </ToolbarTextButton>
        <ToolbarTextButton
          isActive={fit === 'contain'}
          onClick={() => applyFit('contain')}
        >
          Contain
        </ToolbarTextButton>

        <ToolbarDivider />

        {/* Opacity */}
        <ToolbarTextButton
          ref={opacityTriggerRef}
          isActive={opacityOpen}
          aria-label="Opacity"
          onClick={e => {
            if (opacityOpen) {
              setOpacityOpen(false);
            } else {
              const r = e.currentTarget.getBoundingClientRect();
              setOpacityMenuPos({ top: r.bottom + 6, left: r.left });
              setOpacityOpen(true);
            }
          }}
        >
          {opacity}
        </ToolbarTextButton>

        <ToolbarDivider />

        {/* Zoom */}
        <ToolbarZoomStepper
          value={zoom}
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={ZOOM_STEP}
          onChange={applyZoom}
        />

        <ToolbarDivider />

        {/* Delete */}
        <ToolbarIconButton aria-label="Delete" onClick={handleDelete}>
          <DeleteIcon width="15" height="15" strokeColor="currentColor" />
        </ToolbarIconButton>

        {/* More — toggle the full settings panel */}
        {settings && (
          <ToolbarIconButton
            aria-label="More options"
            isActive={isSidebarOnThisElement}
            onClick={() => settings.toggle('photo')}
          >
            <EllipsisIcon width="16" height="4" fillColor="currentColor" />
          </ToolbarIconButton>
        )}
      </FloatingToolbarSurface>
      {opacityMenu}
    </ToolbarLayer>
  );
});

export default ImageFloatingToolbar;
